/**
 * Cast-Zeit („Ausholen") — abgeleitete Regel des Kampfsystems.
 *
 * Jede Attacke holt vor dem Treffer aus: Der Schaden fällt erst `castMs`
 * nach dem Start, der Cooldown läuft ab dem Treffer. Wie beim ersatzlos
 * entfallenen Windup ist die Zeit bewusst KEIN Katalog-Feld, sondern aus der
 * Move-Power abgeleitet — die Move-Daten bleiben unangetastet, und Client wie
 * Server berechnen ohne DB-Roundtrip denselben Wert. `castMs` landet im
 * MoveSnapshot und ist damit hash-relevant.
 *
 * Power-Bänder (C5-A). Die Grenzen liegen bewusst ZWISCHEN den Katalog-
 * Clustern (Slot-1-Spammables ~0,42 · Standard ~1,0 · schwere Moves 1,2–1,5 ·
 * Nuke ab 1,8), damit ein Move nicht durch eine kleine Power-Korrektur das
 * Band wechselt:
 *
 *   Ultimate     → 8s   (C9-A: fest, unabhängig von der Power)
 *   power < 0,9  → 4s
 *   power < 1,2  → 3s
 *   power < 1,8  → 2s
 *   power ≥ 1,8  → 1s
 *
 * Wuchtige Attacken schlagen also schneller zu als leichte: Das Ausholen ist
 * der Preis für einen kurzen Cooldown, nicht für hohen Schaden.
 *
 * ⚠️ Am 10.09.2026 wurde jedes Band um eine Sekunde gesenkt (vorher
 * 5/4/3/2). Die Power-Grenzen sind absichtlich UNVERÄNDERT geblieben: Die
 * Lücken im Katalog liegen bei 0,73–0,94 · 1,1–1,3 · 1,7–2,0, die Grenzen
 * sitzen also weiter mittendrin. Grund für die Senkung ist das serielle
 * Ausholen (siehe unten): Dort ist die Ausholzeit Totzeit im einen Kanal,
 * und ein Sechser-Set blieb mit den alten Bändern darin stecken.
 *
 * Ausgeholt wird PRO KÄMPFER, nicht pro Move (Stefan, 10.09.2026 — ersetzt
 * C1-B vom 02.09.2026): Es holt immer nur EINE Attacke gleichzeitig aus.
 * Der intrinsische Grundangriff (castMs 0) ist davon ausgenommen und feuert
 * weiter. Der Kernel hält die Cast-Phasen weiterhin als Record
 * moveId → Restzeit (simulate.ts), der Record enthält jetzt aber höchstens
 * einen Eintrag.
 *
 * ⚠️ Warum die Rückkehr zu einem Kanal: Gemessen am 10.09.2026 kam die
 * Schwierigkeit der Mehrgegner-Stages fast vollständig daher, dass jeder
 * Kämpfer sein GANZES Set in den ersten fünf Sekunden abfeuert — ein
 * nachrückender Gegner traf damit auf einen Spieler, dessen Set verbraucht
 * war. Auf Zone 3 Stage 7 töteten 76 % der Nachrück-Momente den Spieler
 * binnen sechs Sekunden; mit einem Kanal sind es 0 %.
 *
 * Der intrinsische Grundangriff läuft NICHT über `castMsForMove` — sein
 * Power-Band würde ihm 4s geben. Er trägt fest castMs 0 und bleibt damit die
 * Sofort-Reaktion aus dem Totzonen-Fix (C3-A); gesetzt in
 * `buildMonsterSnapshot` bzw. `buildEnemyMoves`.
 *
 * Der Kernel (`simulate.ts`) liest nur `move.castMs` und `move.cooldownMs`.
 * Die Regeln hier wendet der Snapshot-Bau an, damit die Balance-Rechnung an
 * genau einer Stelle liegt und beide Seiten dasselbe Ergebnis einsetzen.
 */

export const CAST_MS_ULTIMATE = 8_000;
export const CAST_MS_POWER_LOW = 4_000;
export const CAST_MS_POWER_MID = 3_000;
export const CAST_MS_POWER_HIGH = 2_000;
export const CAST_MS_POWER_TOP = 1_000;

export const CAST_POWER_LOW_MAX = 0.9;
export const CAST_POWER_MID_MAX = 1.2;
export const CAST_POWER_HIGH_MAX = 1.8;

export function castMsForMove(move: { power: number; isUltimate?: boolean | null }): number {
  if (move.isUltimate) return CAST_MS_ULTIMATE;
  if (move.power < CAST_POWER_LOW_MAX) return CAST_MS_POWER_LOW;
  if (move.power < CAST_POWER_MID_MAX) return CAST_MS_POWER_MID;
  if (move.power < CAST_POWER_HIGH_MAX) return CAST_MS_POWER_HIGH;
  return CAST_MS_POWER_TOP;
}

/**
 * Ab diesem Basis-Cooldown bleibt der Cooldown unangetastet (C2-A).
 *
 * ⚠️ Am 10.09.2026 von 20 auf 30 Sekunden gehoben, GEMEINSAM mit den
 * Katalog-Abklingzeiten (die auf 12/18/24/27/30 gestiegen sind). Die beiden
 * Zahlen gehören zusammen: Bliebe die Grenze bei 20, fielen 105 der 180
 * Attacken in den Zweig „die Cast-Zeit kommt voll obendrauf", und die
 * sauberen Zyklen 12/18/24/27/30 würden zu krummen 25 bis 33 Sekunden. Der
 * Katalog verspricht in seiner eigenen Regel, dass der Zyklus GLEICH der
 * Abklingzeit ist — das ist die eine Zahl, die ein Spieler nachrechnen kann.
 */
export const CAST_CD_COUPLED_MAX_MS = 30_000;
/** Untergrenze des gekoppelten Cooldowns — nie unter 3s zwischen Treffern. */
export const CAST_CD_MIN_MS = 3_000;

/**
 * „Cast frisst Cooldown" (C2-A): Bis zur Kopplungsgrenze
 * (`CAST_CD_COUPLED_MAX_MS`, seit dem 10.09.2026 30s) wird die Cast-Zeit vom
 * Cooldown abgezogen. **Nur in diesem Band** bleibt der
 * Angriffstakt erhalten (Zyklus = Basis-Cooldown wie bisher, der Treffer
 * fällt nur später darin) — und auch dort nur, solange der 3-Sekunden-Boden
 * nicht greift, also ab Basis-Cooldown ≥ cast + 3s.
 *
 *   castMs = 0   → unverändert. Kritisch für den intrinsischen Grundangriff:
 *                  sein 2,5-s-Takt darf nicht auf 3s gebremst werden.
 *   cd ≤ Grenze  → max(3s, cd − cast); taktneutral, außer der Boden greift
 *                  (dann Zyklus = cast + 3s statt Basis-Cooldown).
 *   cd > Grenze  → unverändert; lange Specials und Ultimates behalten ihren
 *                  Cooldown, das Ausholen kommt VOLL OBENDRAUF. Der Zyklus
 *                  wächst dort um castMs — hier ist der Takt bewusst nicht
 *                  erhalten.
 *
 * ⚠️ Seit dem 10.09.2026 fällt im ganzen Katalog nur noch die Ultimate über
 * die Grenze (Abklingzeit 60s). Alle anderen Zyklen sind exakt gleich ihrer
 * Abklingzeit: 12/18/24/27/30 Sekunden.
 *
 * Merksatz für alle Aufrufer: Der reale Abstand zweier Treffer desselben
 * Moves ist IMMER `castMs + effectiveCooldownMs(...)`, nie der Cooldown
 * allein. Wer Takte nachrechnet (Sims, Aktiv-Bonus-Ableitung im Settle),
 * muss mit dieser Summe rechnen.
 */
export function effectiveCooldownMs(baseCooldownMs: number, castMs: number): number {
  if (castMs === 0) return baseCooldownMs;
  if (baseCooldownMs > CAST_CD_COUPLED_MAX_MS) return baseCooldownMs;
  return Math.max(CAST_CD_MIN_MS, baseCooldownMs - castMs);
}
